import React, { useEffect } from 'react';
import SectionHead from '../components/ui/SectionHead.jsx';
import Placeholder from '../components/ui/Placeholder.jsx';
import Sidebar from '../components/ui/Sidebar.jsx';

const JOURNAL_INFO = [
  { label: 'Full Name', value: 'Journal of Applied Medical and Surgical Disciplines' },
  { label: 'Abbreviation', value: 'JAMSD' },
  { label: 'ISSN', value: 'XXXX-XXXX (pending assignment)' },
  { label: 'Frequency', value: 'Biannual (Spring and Fall)' },
  // { label: 'Publisher', value: 'Infokai Technologies Pvt. Ltd.' },
  { label: 'First Published', value: '2026' },
  { label: 'Access', value: 'Open Access (CC BY 4.0)' },
  { label: 'Article Processing Charges', value: 'None' },
];

const sidebarLinks = [
  { to: '/aims-and-scope', label: 'Aims & Scope' },
  { to: '/editorial-board', label: 'Editorial Board' },
  { to: '/peer-review-policy', label: 'Peer Review Policy' },
  { to: '/author-guidelines', label: 'Author Guidelines' },
  { to: '/contact', label: 'Contact Us' },
];

function About() {
  useEffect(() => {
    document.title = 'About — JAMSD';
  }, []);

  return (
    <>
      {/* Page hero */}
      <section className="bg-navy pt-24 pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-4 items-start">
            <div className="w-1 bg-amber rounded-full self-stretch" aria-hidden="true" />
            <div>
              <p className="font-sans text-amber text-xs font-semibold uppercase tracking-widest mb-2">
                Journal Information
              </p>
              <h1 className="font-display text-white text-3xl md:text-4xl font-bold mb-2">
                About JAMSD
              </h1>
              <p className="font-sans text-slate-light text-base max-w-2xl">
                Learn about the Journal of Applied Medical and Surgical Disciplines — our mission,
                policies, and publishing model.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-10">
          {/* Main content */}
          <div className="flex-1 min-w-0 space-y-8">
            {/* Journal Information Table */}
            <div className="bg-white rounded-lg border border-bg-border shadow-sm overflow-hidden">
              <div className="bg-navy px-6 py-4">
                <h2 className="font-display text-white font-bold text-xl">Journal Information</h2>
              </div>
              <div className="divide-y divide-bg-border">
                {JOURNAL_INFO.map((row) => (
                  <div
                    key={row.label}
                    className="flex flex-col sm:flex-row items-start sm:items-center px-6 py-3 gap-1 sm:gap-4"
                  >
                    <span className="font-sans text-slate font-semibold text-sm sm:w-52 flex-shrink-0">
                      {row.label}
                    </span>
                    <span
                      className={`font-sans text-sm ${
                        row.label === 'Article Processing Charges'
                          ? 'text-green-700 font-bold'
                          : row.label === 'Access'
                          ? 'text-amber font-semibold'
                          : 'text-gray-800'
                      }`}
                    >
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Open Access & Licensing */}
            <div className="bg-white rounded-lg border border-bg-border shadow-sm p-6 md:p-8">
              <SectionHead title="Open Access & Licensing" />
              <div className="flex items-start gap-4 bg-amber-tint rounded-lg border border-amber p-4 mb-5">
                <svg className="w-8 h-8 text-amber flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <div>
                  <h3 className="font-display text-navy font-bold text-base mb-1">Fully Open Access</h3>
                  <p className="font-sans text-amber-tinttext text-sm leading-relaxed">
                    JAMSD is a fully open-access journal. All content is freely accessible worldwide immediately
                    upon publication — with no subscription fees, no paywalls, and no article processing charges (APCs).
                  </p>
                </div>
              </div>
              <div className="font-sans text-gray-700 text-sm leading-relaxed space-y-3">
                <p>
                  Articles are published under the{' '}
                  <a
                    href="https://creativecommons.org/licenses/by/4.0/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-amber font-semibold hover:text-amber-dark transition-colors underline"
                  >
                    Creative Commons Attribution 4.0 International (CC BY 4.0)
                  </a>{' '}
                  license. Under this license, readers are free to share and adapt the published work for any
                  purpose, provided appropriate credit is given to the original authors.
                </p>
                <p>
                  Authors retain full copyright of their work upon publication.
                </p>
              </div>
            </div>

            {/* Publishing Ethics */}
            <div className="bg-white rounded-lg border border-bg-border shadow-sm p-6 md:p-8">
              <SectionHead title="Publishing Ethics" />
              <div className="font-sans text-gray-700 text-sm leading-relaxed space-y-3">
                <p>
                  JAMSD is committed to maintaining the highest standards of publication ethics and takes all
                  possible measures to prevent misconduct.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  {[
                    {
                      icon: '📋',
                      title: 'COPE Guidelines',
                      desc: 'JAMSD follows the Committee on Publication Ethics (COPE) guidelines for all editorial decisions and misconduct cases.',
                    },
                    {
                      icon: '⚖️',
                      title: 'ICMJE Recommendations',
                      desc: 'We adhere to ICMJE recommendations for authorship, peer review, and conflict of interest disclosure.',
                    },
                    {
                      icon: '🔬',
                      title: 'Declaration of Helsinki',
                      desc: 'All human subjects research must conform to the ethical principles outlined in the Declaration of Helsinki.',
                    },
                    {
                      icon: '🚫',
                      title: 'Zero Tolerance for Misconduct',
                      desc: 'Plagiarism, data fabrication, falsification, and duplicate submission lead to immediate rejection or retraction.',
                    },
                  ].map((item) => (
                    <div key={item.title} className="bg-bg-cool rounded-lg p-4 border border-bg-border">
                      <div className="text-2xl mb-2" role="img" aria-hidden="true">{item.icon}</div>
                      <h4 className="font-display text-navy font-semibold text-sm mb-1">{item.title}</h4>
                      <p className="font-sans text-gray-600 text-xs leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* EDGE Special Section */}
            <div className="bg-white rounded-lg border border-bg-border shadow-sm p-6 md:p-8">
              <SectionHead title="EDGE Special Section" />
              <div className="bg-navy rounded-lg p-5 mb-5">
                <div className="flex items-center gap-3 mb-3">
                  <span className="font-display text-amber font-bold text-2xl">EDGE</span>
                  <div className="h-px flex-1 bg-navy-border" />
                </div>
                <p className="font-display text-slate-light text-sm italic">
                  Evidence, Debate, Guidance &amp; Experience
                </p>
              </div>
              <div className="font-sans text-gray-700 text-sm leading-relaxed space-y-3">
                <p>
                  The <strong>EDGE special section</strong> is a curated content series within JAMSD featuring
                  thought-provoking, practitioner-focused material that goes beyond traditional research articles.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                  {[
                    { letter: 'E', title: 'Evidence', desc: 'Systematic breakdowns of current evidence on key clinical questions.' },
                    { letter: 'D', title: 'Debate', desc: 'Thought-leader debates exploring opposing perspectives on contentious issues.' },
                    { letter: 'G', title: 'Guidance', desc: 'Practical guidance frameworks for clinicians navigating complex decisions.' },
                    { letter: 'E', title: 'Experience', desc: 'Frontline experience reflections from practitioners across disciplines.' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3 bg-bg-ice rounded-lg p-3 border border-bg-border">
                      <div className="w-8 h-8 rounded-full bg-amber text-white font-display font-bold text-sm flex items-center justify-center flex-shrink-0">
                        {item.letter}
                      </div>
                      <div>
                        <h4 className="font-display text-navy font-semibold text-sm">{item.title}</h4>
                        <p className="font-sans text-gray-600 text-xs mt-0.5 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Indexing */}
            <div className="bg-white rounded-lg border border-bg-border shadow-sm p-6 md:p-8">
              <SectionHead title="Indexing & Abstracting" />
              {/* <Placeholder label="Indexing & abstracting status will be updated when available" /> */}
              <p className="font-sans text-gray-600 text-sm leading-relaxed mt-4">
                JAMSD is a newly launched journal (2026). We are actively pursuing indexing in major
                databases and abstracting services. Updates will be posted as indexing applications are
                submitted and approved.
              </p>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:w-72 xl:w-80 flex-shrink-0">
            <Sidebar quickLinks={sidebarLinks} showSubmitCta />
          </div>
        </div>
      </div>
    </>
  );
}

export default About;
